import { WorkloadCreationDrawerEntities } from "@/models/workload-creation-drawer-model";
import type { THybridSelectedTemplates } from "@/models/workload-hybrid.model";
import { type IUISuperWorkloadCreationRequest } from "@/models/workload.model";
import { AssetKind, Scope } from "@/swagger-models/assets-service-client";
import type { ScopeType, UnifiedWorkloadRequest } from "@/swagger-models/workloads-client";
import { alertUtil } from "@/utils/alert.util";
import { deepCopy } from "@/utils/common.util";
import { workloadHybridUtil as whUtil } from "@/utils/workload-hybrid.util/workload-hybrid.util";
import { useQuasar } from "quasar";
import { computed, type Ref, ref } from "vue";
import type { EHybridFormTypes } from "./helpers/use-hybrid-templates.mapper";
import { useHybridTemplatesMapper } from "./helpers/use-hybrid-templates.mapper";
import { useRouter } from "vue-router";
import { scopeUtil } from "@/utils/scope.util";

export function useHybridTemplates(
  workloadType: EHybridFormTypes,
  initialModel: IUISuperWorkloadCreationRequest = whUtil.getEmptyUISuperWorkloadModel(),
  isTemplate = false,
) {
  const $q = useQuasar();
  const $router = useRouter();

  // rotemTODO: consider changing to reactive instead of ref
  const hybridWorkloadModel: Ref<IUISuperWorkloadCreationRequest> = ref(initialModel);
  const selectedTemplates: Ref<THybridSelectedTemplates> = ref(whUtil.getWorkloadHybridTemplatesSetup());
  const drawerDataEntity: Ref<WorkloadCreationDrawerEntities | null> = ref(null);
  const isDrawerOpen: Ref<boolean> = ref(false);
  const applyingSelection: Ref<boolean> = ref(false);
  const recentlyCreatedEntityId: Ref<string | null> = ref(null);

  const closeDrawer = (): void => {
    isDrawerOpen.value = false;
    drawerDataEntity.value = null;
  };

  const openDrawer = (entity: WorkloadCreationDrawerEntities): void => {
    drawerDataEntity.value = entity;
    isDrawerOpen.value = true;
  };

  const displayEnvironmentsList = (): void => {
    openDrawer(WorkloadCreationDrawerEntities.Environment);
  };
  const displayComputesList = (): void => {
    openDrawer(WorkloadCreationDrawerEntities.Compute);
  };
  const displayDataSourcesList = (): void => {
    openDrawer(WorkloadCreationDrawerEntities.DataSource);
  };

  const updateEnvironmentRenderKey = (): void => {
    selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].renderKey++;
  };
  const reRenderSectionEntirely = (section: WorkloadCreationDrawerEntities): void => {
    selectedTemplates.value[section].sectionKey++;
  };

  const getUnifiedHybridWorkload = useHybridTemplatesMapper[workloadType].getUnified;

  const combineModels = async (assetId: string | string[], dataVolumeId?: string | string[]): Promise<void> => {
    const requestOptions: UnifiedWorkloadRequest = {
      clusterId: hybridWorkloadModel.value.clusterId,
    };
    if (!isTemplate) requestOptions.projectId = hybridWorkloadModel.value.projectId;

    Array.isArray(assetId) ? (requestOptions.assetIds = assetId) : (requestOptions.assetId = assetId);
    if (dataVolumeId) {
      Array.isArray(dataVolumeId)
        ? (requestOptions.dataVolumeIds = dataVolumeId)
        : (requestOptions.dataVolumeIds = [dataVolumeId]);
    }

    const modelWithItemDetailsFlattened = await getUnifiedHybridWorkload(requestOptions);

    const uiModel = useHybridTemplatesMapper[workloadType].getUIModel(modelWithItemDetailsFlattened);

    switch (drawerDataEntity.value) {
      case WorkloadCreationDrawerEntities.Environment: {
        const envFields = useHybridTemplatesMapper[workloadType].getRelevantEnvironmentFields(uiModel);
        hybridWorkloadModel.value = whUtil.combineWorkloadModelWithEnvironmentDetails(
          hybridWorkloadModel.value,
          envFields,
        );
        selectedTemplates.value.environment.defaultData = envFields;
        updateEnvironmentRenderKey();
        break;
      }
      case WorkloadCreationDrawerEntities.Compute: {
        const computeFields = whUtil.getRelevantComputeFields(uiModel);
        hybridWorkloadModel.value = whUtil.combineWorkloadModelWithComputeDetails(
          hybridWorkloadModel.value,
          computeFields,
        );
        selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].defaultData = computeFields;
        break;
      }
      case WorkloadCreationDrawerEntities.DataSource: {
        const storageFields = whUtil.getRelevantStorageFields(uiModel);
        selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].defaultData = storageFields;
        hybridWorkloadModel.value = whUtil.combineWorkloadModelWithStorageDetails(
          hybridWorkloadModel.value,
          storageFields,
        );
        break;
      }
    }
  };

  const handleSelection = async (itemIdAndName: { id: string; name: string }): Promise<void> => {
    if (!drawerDataEntity.value || applyingSelection.value) return;
    applyingSelection.value = true;
    try {
      await combineModels(itemIdAndName.id);
      selectedTemplates.value[drawerDataEntity.value].name = itemIdAndName.name;
      closeDrawer();
    } catch (e) {
      $q.notify(alertUtil.getError("Failed to select asset template"));
      console.error("Failed to select asset template:\n", e);
    } finally {
      applyingSelection.value = false;
    }
  };

  const handleMultiSelection = async (selectedItems: { id: string; name: string; kind: AssetKind }[]): Promise<void> => {
    if (!drawerDataEntity.value || applyingSelection.value) return;
    applyingSelection.value = true;
    try {
      await combineModels(
        selectedItems.filter((item) => item.kind !== AssetKind.DataVolume).map((item) => item.id),
        selectedItems.filter((item) => item.kind === AssetKind.DataVolume).map((item) => item.id),
      );

      closeDrawer();
    } catch (e) {
      $q.notify(alertUtil.getError("Failed to select asset template"));
      console.error("Failed to select asset template:\n", e);
    } finally {
      applyingSelection.value = false;
    }
  };

  const resetEnvironmentToDefault = (): void => {
    if (!selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].defaultData) return;

    hybridWorkloadModel.value = whUtil.combineWorkloadModelWithEnvironmentDetails(
      hybridWorkloadModel.value,
      selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].defaultData,
    );
    updateEnvironmentRenderKey();
  };

  const resetComputeToDefault = (): void => {
    if (!selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].defaultData) return;

    hybridWorkloadModel.value = whUtil.combineWorkloadModelWithComputeDetails(
      hybridWorkloadModel.value,
      selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].defaultData,
    );
  };

  const resetDataSourcesToDefault = (): void => {
    if (!selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].defaultData) return;

    hybridWorkloadModel.value = whUtil.combineWorkloadModelWithStorageDetails(
      hybridWorkloadModel.value,
      selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].defaultData,
    );
  };

  const clearEnvironmentTemplate = (): void => {
    selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].name = "";
    selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].defaultData = null;

    if (!selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].clearedState) return;

    hybridWorkloadModel.value = whUtil.combineWorkloadModelWithEnvironmentDetails(
      hybridWorkloadModel.value,
      selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].clearedState,
    );
    updateEnvironmentRenderKey();
    reRenderSectionEntirely(WorkloadCreationDrawerEntities.Environment);
  };

  const clearComputeTemplate = (): void => {
    selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].name = "";
    selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].defaultData = null;

    if (!selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].clearedState) return;

    hybridWorkloadModel.value = whUtil.combineWorkloadModelWithComputeDetails(
      hybridWorkloadModel.value,
      selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].clearedState,
    );
    reRenderSectionEntirely(WorkloadCreationDrawerEntities.Compute);
  };

  const clearDataSourceTemplate = (): void => {
    selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].name = "";
    selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].defaultData = null;

    if (!selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].clearedState) return;

    hybridWorkloadModel.value = whUtil.combineWorkloadModelWithStorageDetails(
      hybridWorkloadModel.value,
      selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].clearedState,
    );
  };

  const createSectionsClearedStates = (): void => {
    const base = deepCopy(hybridWorkloadModel.value);
    selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].clearedState =
      useHybridTemplatesMapper[workloadType].getRelevantEnvironmentFields(base);
    selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].clearedState = whUtil.getRelevantComputeFields(base);
    selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].clearedState =
      whUtil.getRelevantStorageFields(base);
  };

  const handleCreatedAssets = (kind?: string, createdEntityId?: string): void => {
    if (!kind || !createdEntityId) return;

    recentlyCreatedEntityId.value = createdEntityId;
    switch (kind) {
      case AssetKind.Environment:
        displayEnvironmentsList();
        break;
      case AssetKind.Compute:
        displayComputesList();
        break;
      case AssetKind.HostPath:
      case AssetKind.Nfs:
      case AssetKind.Git:
      case AssetKind.S3:
      case AssetKind.Pvc:
      case AssetKind.ConfigMap:
      case AssetKind.SecretVolume:
        displayDataSourcesList();
        break;
      default:
        recentlyCreatedEntityId.value = null;
    }
  };

  const onCreateNewSetup = (type: string, additionalQuery = {}): void => {
    switch (drawerDataEntity.value) {
      case WorkloadCreationDrawerEntities.Environment:
        onCreateNewEnvironment(additionalQuery);
        break;
      case WorkloadCreationDrawerEntities.Compute:
        onCreateNewComputeResource(additionalQuery);
        break;
      case WorkloadCreationDrawerEntities.DataSource:
        onCreateNewDataSource(type, additionalQuery);
        break;
    }
  };

  const _getCreationRouteName = (assetType: "environment" | "compute" | string): string | undefined => {
    const routes = isTemplate
      ? useHybridTemplatesMapper[workloadType].templateCreationRoutes
      : useHybridTemplatesMapper[workloadType].creationRoutes;

    if (!routes) return undefined;

    if (assetType !== "environment" && assetType !== "compute") {
      return routes.datasource.pages[assetType];
    }
    return routes[assetType]?.name;
  };

  const _getCreationScopeQuery = (): Record<string, string> | undefined => {
    if (isTemplate) {
      const scopeModel = scopeUtil.getScopeModelFromTypeAndId(
        hybridWorkloadModel.value.scopeType as ScopeType,
        hybridWorkloadModel.value.scopeId as string,
      );

      return {
        ...Object.fromEntries(
          Object.entries(scopeModel)
            .filter(([_, value]) => value != null)
            .map(([key, value]) => [key, String(value)]),
        ),
      };
    } else {
      return {
        projectId: hybridWorkloadModel.value.projectId,
        scope: Scope.Project,
      };
    }
  };

  const onCreateNewEnvironment = (additionalQuery = {}): void => {
    const routeName = _getCreationRouteName("environment");
    let query = _getCreationScopeQuery();

    const queryFromMapper = isTemplate
      ? useHybridTemplatesMapper[workloadType].templateCreationRoutes?.environment?.query
      : useHybridTemplatesMapper[workloadType].creationRoutes?.environment?.query;

    if (queryFromMapper) {
      query = {
        ...queryFromMapper,
        ...query,
      };
    }

    if (!routeName || !query) return;

    $router.push({
      name: routeName,
      query: {
        ...query,
        ...additionalQuery,
      },
    });
  };

  const onCreateNewComputeResource = (additionalQuery = {}): void => {
    const routeName = _getCreationRouteName("compute");
    let query = _getCreationScopeQuery();

    const queryFromMapper = isTemplate
      ? useHybridTemplatesMapper[workloadType].templateCreationRoutes?.compute?.query
      : useHybridTemplatesMapper[workloadType].creationRoutes?.compute?.query;

    if (queryFromMapper) {
      query = {
        ...queryFromMapper,
        ...query,
      };
    }

    if (!routeName || !query) return;

    $router.push({
      name: routeName,
      query: {
        ...query,
        ...additionalQuery,
      },
    });
  };

  const onCreateNewDataSource = (selectedDataSourceType: string, additionalQuery = {}): void => {
    const routeName = _getCreationRouteName(selectedDataSourceType);
    if (!routeName) return;
    const query = _getCreationScopeQuery();

    if (!routeName || !query) return;

    $router.push({
      name: routeName,
      query: {
        fromPage: routeName,
        ...query,
        ...additionalQuery,
      },
    });
  };

  // computed
  const selectedEnvironmentName = computed(() => {
    return selectedTemplates.value[WorkloadCreationDrawerEntities.Environment].name;
  });

  const selectedComputeName = computed(() => {
    return selectedTemplates.value[WorkloadCreationDrawerEntities.Compute].name;
  });

  const selectedDataSourceName = computed(() => {
    return selectedTemplates.value[WorkloadCreationDrawerEntities.DataSource].name;
  });

  return {
    // state
    isDrawerOpen,
    drawerDataEntity,
    applyingSelection,
    hybridWorkloadModel,
    selectedTemplates,
    recentlyCreatedEntityId,
    // methods
    closeDrawer,
    openDrawer,
    displayEnvironmentsList,
    displayComputesList,
    displayDataSourcesList,
    handleSelection,
    handleMultiSelection,
    createSectionsClearedStates,
    clearComputeTemplate,
    clearDataSourceTemplate,
    clearEnvironmentTemplate,
    resetEnvironmentToDefault,
    resetComputeToDefault,
    resetDataSourcesToDefault,
    handleCreatedAssets,
    getUnifiedHybridWorkload,
    reRenderSectionEntirely,
    onCreateNewSetup,
    // computed
    selectedEnvironmentName,
    selectedComputeName,
    selectedDataSourceName,
  };
}
